import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import '../styles/ItemModal.css';
import { StoreContext } from '../context/StoreContext';
import { getCrossSellItems } from '../api/apiService';
import { formatPrice } from '../utils/formatPrice'; // <-- Import the helper

const SPICY_LEVELS = [
    { label: 'Mild', value: 'mild' },
    { label: 'Medium', value: 'medium' },
    { label: 'Hot', value: 'hot' },
];

const CrossSellItem = ({ item, quantity, setQuantity, currency, currencySymbol = '₹' }) => (
    <div className="cross-sell-item">
        <div className="cross-sell-item-info">
            <p className="cross-sell-item-name">{item.name}</p>
            {/* --- MODIFIED --- */}
            <p className="cross-sell-item-price">{formatPrice(item.price, currency, currencySymbol)}</p>
            {/* --- END --- */}
        </div>
        <div className="cross-sell-controls">
            {quantity === 0 ? (
                <Button variant="outline-success" size="sm" onClick={() => setQuantity(1)}>Add</Button>
            ) : (
                <>
                    <Button variant="outline-secondary" size="sm" onClick={() => setQuantity(Math.max(0, quantity - 1))}>-</Button>
                    <span className="quantity-display-small">{quantity}</span>
                    <Button variant="outline-secondary" size="sm" onClick={() => setQuantity(quantity + 1)}>+</Button>
                </>
            )}
        </div>
    </div>
);

// --- MODIFIED SIGNATURE ---
const ItemModal = ({
    show,
    handleClose,
    item,
    handleAddToCart,
    vendor_id,
    vendor_type,
    cart,
    currency,
    currencySymbol = '₹'
}) => {
// --- END ---
    const { addItemToCart } = useContext(StoreContext);

    const [quantity, setQuantity] = useState(1);
    const [spicyLevel, setSpicyLevel] = useState('medium');
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [crossSellItems, setCrossSellItems] = useState({});
    const [isLoadingCrossSell, setIsLoadingCrossSell] = useState(false);
    const [crossSellSelections, setCrossSellSelections] = useState({});
    const [initialCartState, setInitialCartState] = useState(null);

    useEffect(() => {
        if (!show) {
            setInitialCartState(null);
            setCrossSellSelections({});
            return;
        }

        if (item) {
            // ✅ IMPROVEMENT: More specific find logic to prevent conflicts with cross-sell items.
            const mainCartItem = cart?.items?.find(
                (ci) => String(ci.item_id) === String(item._id) && !ci.isCrossSell
            );

            setQuantity(mainCartItem ? mainCartItem.quantity : 1);
            setSpicyLevel(mainCartItem ? (mainCartItem.spicyLevel || 'medium') : 'medium');
            setSpecialInstructions(mainCartItem ? (mainCartItem.notes || '') : '');

            const fetchAndSetStates = async () => {
                let initialSelections = {};
                if (vendor_type === 'restaurant' || vendor_type === 'cloud_kitchen') {
                    setIsLoadingCrossSell(true);
                    try {
                        const { data: fetchedItems } = await getCrossSellItems(vendor_type, vendor_id);
                        const selections = {};
                        Object.values(fetchedItems).flat().forEach(csItem => {
                            const cartItem = cart?.items?.find(ci => String(ci.item_id) === String(csItem._id));
                            selections[csItem._id] = cartItem ? cartItem.quantity : 0;
                        });
                        setCrossSellItems(fetchedItems);
                        setCrossSellSelections(selections);
                        initialSelections = selections;
                    } catch (error) {
                        console.error('Failed to fetch cross-sell items:', error);
                    } finally {
                        setIsLoadingCrossSell(false);
                    }
                } else {
                    setCrossSellSelections({});
                    initialSelections = {};
                }

                if (mainCartItem) {
                    setInitialCartState({
                        quantity: mainCartItem.quantity,
                        spicyLevel: mainCartItem.spicyLevel || 'medium',
                        specialInstructions: mainCartItem.notes || '',
                        crossSellSelections: initialSelections,
                    });
                } else {
                    // Even if it's a new item, we need the initial state of cross-sell items
                    // to calculate the button total correctly.
                    setInitialCartState({
                         crossSellSelections: initialSelections
                    });
                }
            };
            fetchAndSetStates();
        }
    }, [show, item, vendor_id, vendor_type, cart]);

    if (!item) return null;

    const mainCartItem = cart?.items?.find(ci => String(ci.item_id) === String(item._id) && !ci.isCrossSell);
    const isUpdateMode = !!mainCartItem;

    const handleDecrement = () => setQuantity(q => Math.max(1, q - 1));
    const handleIncrement = () => setQuantity(q => q + 1);

    const isCustomizable = item.hasSpicyLevels === true;
    const isFoodItem = vendor_type === 'restaurant' || vendor_type === 'cloud_kitchen';

    const getValidPrice = (val) => {
        const price = Number(val);
        return isNaN(price) ? 0 : price;
    };

    // ✅ FINAL FIX: This function now has simpler and more robust logic.
    const calculateGrandTotal = () => {
        // Start with the price of the main item being configured.
        let grandTotal = getValidPrice(item.price_per_unit ?? item.price) * quantity;
        
        // Add the value of all cross-sell items currently selected in the modal.
        Object.entries(crossSellSelections).forEach(([id, qty]) => {
            if (qty > 0) {
                const csItem = Object.values(crossSellItems).flat().find(i => String(i._id) === String(id));
                if (csItem) {
                    grandTotal += getValidPrice(csItem.price_per_unit ?? csItem.price) * qty;
                }
            }
        });

        // If in UPDATE mode, the initialCartState contains the state of the cart *before*
        // this modal was opened. We must subtract that initial value to avoid double counting.
        if (isUpdateMode && initialCartState) {
             Object.entries(initialCartState.crossSellSelections).forEach(([id, qty]) => {
                if (qty > 0) {
                     const csItem = Object.values(crossSellItems).flat().find(i => String(i._id) === String(id));
                    if (csItem) {
                        grandTotal -= getValidPrice(csItem.price_per_unit ?? csItem.price) * qty;
                    }
                }
            });
        }

        return grandTotal.toFixed(2);
    };

    const handleConfirmAndAddToCart = async () => {
        const mainItemPayload = {
            ...item,
            quantity: quantity,
            spicyLevel: isCustomizable ? spicyLevel : undefined,
            specialInstructions: isFoodItem ? specialInstructions : undefined,
            notes: isFoodItem ? specialInstructions : undefined,
            isCrossSell: false // Explicitly mark as not a cross-sell item
        };

        handleAddToCart(mainItemPayload);

        const allCrossSellItemIds = new Set([
            ...Object.keys(crossSellSelections),
            ...Object.keys(initialCartState?.crossSellSelections || {})
        ]);

        const crossSellPromises = [];
        for (const id of allCrossSellItemIds) {
            const finalQty = crossSellSelections[id] || 0;
            const initialQty = (initialCartState?.crossSellSelections?.[id]) || 0;

            if (finalQty !== initialQty) {
                const csItem = Object.values(crossSellItems).flat().find(i => String(i._id) === String(id));
                if (csItem) {
                    const crossSellPayload = {
                        ...csItem,
                        item_id: csItem._id,
                        quantity: finalQty,
                        vendor_id,
                        vendor_type,
                        price_per_unit: getValidPrice(csItem.price),
                        isCrossSell: true,
                    };
                    crossSellPromises.push(addItemToCart(crossSellPayload));
                }
            }
        }

        if (crossSellPromises.length > 0) {
            try {
                await Promise.all(crossSellPromises);
            } catch (err) {
                console.error("Error modifying cross-sell items from modal:", err);
            }
        }
    };

    const buttonActionText = isUpdateMode ? 'Update Cart' : 'Add to Cart';

    let hasChanges = true;
    if (isUpdateMode && initialCartState) {
        const quantityChanged = quantity !== initialCartState.quantity;
        const spicyLevelChanged = isCustomizable && spicyLevel !== initialCartState.spicyLevel;
        const instructionsChanged = isFoodItem && specialInstructions !== initialCartState.specialInstructions;
        const crossSellChanged = JSON.stringify(crossSellSelections) !== JSON.stringify(initialCartState.crossSellSelections);
        hasChanges = quantityChanged || spicyLevelChanged || instructionsChanged || crossSellChanged;
    }

    const isButtonDisabled = item.isAvailable === false || (isUpdateMode && !hasChanges);
    const priceToDisplay = getValidPrice(item.price_per_unit ?? item.price);
    const grandTotal = calculateGrandTotal();

    return (
        <Modal show={show} onHide={handleClose} centered size="md" className="item-modal">
            <Modal.Body className="modal-body-custom">
                <button className="close-btn" onClick={handleClose} aria-label="Close">×</button>
                <div className="modal-scrollable-content">
                    <div className="modal-content-wrapper">
                        <div className="modal-image-section">
                            <img src={item.image || item.img || 'https://via.placeholder.com/180'} alt={item.name} className="modal-item-image" />
                        </div>
                        <div className="modal-details-section">
                            <h4 className="item-name">{item.name}</h4>
                            {/* --- MODIFIED --- */}
                            <div className="item-price-rating">
                                <span className="price">{formatPrice(priceToDisplay, currency, currencySymbol)}</span>
                            </div>
                            {/* --- END --- */}
                            {item.description && <p className="item-description">{item.description}</p>}
                            {isCustomizable && (
                                <div className="spicy-level-section">
                                    <label className="spicy-label">Spicy Level:</label>
                                    <div className="spicy-options">
                                        {SPICY_LEVELS.map(level => (
                                            <label key={level.value}>
                                                <input type="radio" name={"spicy-level-" + item._id} value={level.value} checked={spicyLevel === level.value} onChange={e => setSpicyLevel(e.target.value)} />
                                                {level.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {isFoodItem && (
                                <div className="special-instructions-section">
                                    <label htmlFor="special-instructions" className="instructions-label">Special Instructions:</label>
                                    <textarea id="special-instructions" className="instructions-textarea" value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)} placeholder="e.g., no onions, extra spicy" rows={2} />
                                </div>
                            )}
                        </div>
                    </div>
                    {isFoodItem && (
                        <div className="cross-sell-section">
                            {isLoadingCrossSell ? <p>Loading suggestions...</p> :
                                Object.entries(crossSellItems).map(([category, items]) => (
                                    items.length > 0 && (
                                        <div key={category} className="cross-sell-category">
                                            <h5 className="cross-sell-title">Recommended {category}</h5>
                                            <div className="cross-sell-list">
                                                {items.map(csItem => {
                                                    const quantity = crossSellSelections[csItem._id] || 0;
                                                    const setQuantity = (q) => {
                                                        setCrossSellSelections(prev => ({ ...prev, [csItem._id]: Math.max(0, q) }));
                                                    };
                                                    // --- MODIFIED: Pass currency and currencySymbol ---
                                                    return (
                                                        <CrossSellItem
                                                            key={csItem._id}
                                                            item={csItem}
                                                            quantity={quantity}
                                                            setQuantity={setQuantity}
                                                            currency={currency}
                                                            currencySymbol={currencySymbol}
                                                        />
                                                    );
                                                    // --- END ---
                                                })}
                                            </div>
                                        </div>
                                    )
                                ))
                            }
                        </div>
                    )}
                </div>
                <div className="modal-footer-custom">
                    <div className="quantity-selector-footer">
                        <Button variant="outline-secondary" onClick={handleDecrement} disabled={quantity <= 1}>-</Button>
                        <span className="quantity-display-footer">{quantity}</span>
                        <Button variant="outline-secondary" onClick={handleIncrement} disabled={item.isAvailable === false}>+</Button>
                    </div>
                    {/* --- MODIFIED --- */}
                    <Button variant="success" className="add-button-footer" onClick={handleConfirmAndAddToCart} disabled={isButtonDisabled}>
                        {buttonActionText} - {formatPrice(grandTotal, currency, currencySymbol)}
                    </Button>
                    {/* --- END --- */}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ItemModal;

